import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  AutoFixHigh as NormalizeIcon,
  Login as LoginIcon,
  ArrowForward as ArrowIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TrialAddressNormalizer from '../components/TrialAddressNormalizer';
import { trialService, NormalizedAddress } from '../services/trial.service';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [remainingAttempts, setRemainingAttempts] = useState(5);

  useEffect(() => {
    // Load remaining attempts on component mount
    const loadRemainingAttempts = async () => {
      try {
        const response = await trialService.getRemainingAttempts();
        setRemainingAttempts(response.remainingAttempts);
      } catch (error) {
        console.error('Failed to load remaining attempts:', error);
      }
    };

    loadRemainingAttempts();
  }, []);

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Chuẩn hóa nhanh chóng',
      description: 'Xử lý hàng nghìn địa chỉ chỉ trong vài phút với công nghệ AI tiên tiến'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Bảo mật tuyệt đối',
      description: 'Dữ liệu của bạn được mã hóa và bảo vệ an toàn, không bao giờ bị rò rỉ'
    },
    {
      icon: <UploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Dễ dàng sử dụng',
      description: 'Tải file Excel hoặc nhập trực tiếp, giao diện thân thiện với người dùng'
    },
    {
      icon: <DownloadIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Xuất kết quả',
      description: 'Tải về file Excel với địa chỉ đã được chuẩn hóa hoàn chỉnh'
    }
  ];

  const benefits = [
    'Tiết kiệm thời gian xử lý địa chỉ',
    'Giảm thiểu lỗi nhập liệu thủ công',
    'Chuẩn hóa theo quy chuẩn quốc gia',
    'Hỗ trợ địa chỉ toàn quốc',
    'Tích hợp dễ dàng với hệ thống hiện có',
    'Báo cáo chi tiết và thống kê'
  ];

  const stats = [
    { number: '10,000+', label: 'Địa chỉ đã xử lý' },
    { number: '99.5%', label: 'Độ chính xác' },
    { number: '50+', label: 'Khách hàng tin tưởng' },
    { number: '24/7', label: 'Hỗ trợ khách hàng' }
  ];

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleVideoClick = () => {
    window.open('https://www.youtube.com/watch?v=8oXQC0IOMEA', '_blank');
  };

  const handleNormalizeAddress = async (address: string): Promise<NormalizedAddress> => {
    const result = await trialService.normalizeAddress(address);
    // Update remaining attempts after successful normalization
    const remainingResponse = await trialService.getRemainingAttempts();
    setRemainingAttempts(remainingResponse.remainingAttempts);
    return result;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            zIndex: 1
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ py: { xs: 8, md: 12 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={4}>
                  <Box>
                    <Chip
                      icon={<StarIcon />}
                      label="Công cụ chuẩn hóa địa chỉ hàng đầu"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        mb: 2,
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                    <Typography
                      variant="h2"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        lineHeight: 1.2
                      }}
                    >
                      Chuẩn hóa địa chỉ
                      <br />
                      <Box component="span" sx={{ color: 'secondary.light' }}>
                        tự động
                      </Box>
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        opacity: 0.9,
                        fontWeight: 400,
                        lineHeight: 1.6
                      }}
                    >
                      Giải pháp thông minh giúp chuẩn hóa địa chỉ Việt Nam một cách nhanh chóng, 
                      chính xác và tiết kiệm thời gian cho doanh nghiệp của bạn.
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleLoginClick}
                      startIcon={<LoginIcon />}
                      endIcon={<ArrowIcon />}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[8]
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Bắt đầu ngay
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleVideoClick}
                      startIcon={<PlayIcon />}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Xem demo
                    </Button>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <TrialAddressNormalizer
                    onNormalize={handleNormalizeAddress}
                    remainingAttempts={remainingAttempts}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight={600}>
            Tính năng nổi bật
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Khám phá những tính năng mạnh mẽ giúp chuẩn hóa địa chỉ hiệu quả
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom fontWeight={600}>
                Lợi ích khi sử dụng
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                Giải pháp chuẩn hóa địa chỉ mang lại nhiều lợi ích thiết thực cho doanh nghiệp
              </Typography>
              
              <List>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white'
                }}
              >
                <Typography variant="h4" gutterBottom fontWeight={600}>
                  Thống kê ấn tượng
                </Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  {stats.map((stat, index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" component="div" fontWeight={700}>
                          {stat.number}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={4}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white'
          }}
        >
          <Typography variant="h3" component="h2" gutterBottom fontWeight={600}>
            Sẵn sàng bắt đầu?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Tham gia ngay để trải nghiệm công nghệ chuẩn hóa địa chỉ tiên tiến
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleLoginClick}
            startIcon={<LoginIcon />}
            sx={{
              bgcolor: 'white',
              color: 'secondary.main',
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'grey.100',
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[8]
              },
              transition: 'all 0.3s ease'
            }}
          >
            Đăng ký miễn phí
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default LandingPage; 